import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  CircularProgress,
  Typography
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { contactService } from '../services/contactService';

const ContactSelector = ({ companyId, opportunityId, onContactAdded, showUnassignedOnly = false }) => {
  const [availableContacts, setAvailableContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadContacts();
  }, [companyId, opportunityId, showUnassignedOnly]);

  const loadContacts = async () => {
    setLoading(true);
    setError('');
    try {
      // If showUnassignedOnly is true, get contacts without any company
      // Otherwise, get contacts for the specified company or without company if companyId is null
      const contactsData = showUnassignedOnly 
        ? await contactService.getContactsByCompany(null, opportunityId)
        : await contactService.getContactsByCompany(companyId || null, opportunityId);
      
      const transformedContacts = contactsData.map(contact => ({
        contactId: contact.id || contact.Id || contact.contactId,
        firstName: contact.firstName || contact.FirstName,
        lastName: contact.lastName || contact.LastName,
        email: contact.email || contact.Email,
        title: contact.title || contact.Title,
        mainPhone: contact.mainPhone || contact.MainPhone
      }));

      setAvailableContacts(transformedContacts);
    } catch (err) {
      console.error('Error loading contacts:', err);
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Autocomplete
      options={availableContacts.sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.trim();
        const nameB = `${b.firstName} ${b.lastName}`.trim();
        return nameA.localeCompare(nameB);
      })}
      getOptionLabel={(option) => option && `${option.firstName} ${option.lastName}`.trim()}
      isOptionEqualToValue={(option, value) => 
        option.contactId === value.contactId
      }
      onChange={(event, newValue) => {
        if (newValue) onContactAdded(newValue);
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body1">
              {option.firstName} {option.lastName}
            </Typography>
            {option.title && (
              <Typography variant="body2" color="text.secondary">
                {option.title}
              </Typography>
            )}
            {option.email && (
              <Typography variant="body2" color="text.secondary">
                ✉️ {option.email}
              </Typography>
            )}
            {option.mainPhone && (
              <Typography variant="body2" color="text.secondary">
                📞 {option.mainPhone}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Add Contact"
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{
            ...params.InputProps,
            startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{ mb: 2 }}
    />
  );
};

export default ContactSelector; 
